import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  extractValidationErrors,
  fetchQuery,
  useMutation
} from '@sequencehq/api'
import { useQuery } from '@sequencehq/api/utils'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useNotifications } from '@sequencehq/core-components'
import { MINUTES } from '@sequencehq/utils/dates'

import { apiQueryClient } from 'features/api/apiQueryClient'
import { useCreateAggregatedLineItemGroups } from 'CreditNotes/hooks/useCreateAggregatedLineItemGroups'

import { CreditNoteLineItemRow } from './CreditNoteLineItemTable'
import { createCreditNoteWithLineItems } from './createCreditNoteWithLineItems'

/**
 * The "Issue and send" submission flow for the refund-manually path:
 *
 *   1. create a draft credit note with the selected line items
 *      (createCreditNoteWithLineItems, shared with the issue-and-apply flow)
 *   2. optionally enable a cash credit grant on the draft
 *   3. finalize and send it (the legacy flow), emailing it to the customer
 *
 * Unlike issue-and-apply, nothing is applied to the invoice or the ledger: the
 * refund is handled manually outside Sequence. This mirrors the legacy
 * credit-note issue dialog (postFinalizeAndSendCreditNote).
 */
export const useIssueAndSendCreditNote = ({
  invoiceId
}: {
  invoiceId: string
}) => {
  const { displayNotification } = useNotifications()
  const navigate = useNavigate()
  const createLineItemGroups = useCreateAggregatedLineItemGroups()

  // Shares the cached invoice query with the rest of the drawer flow.
  const invoiceQuery = useQuery(
    dashboardv99990101Client.getInvoice,
    { id: invoiceId },
    { staleTime: 1 * MINUTES }
  )
  const invoice = invoiceQuery.data

  const { mutateAsync: postCreditNote } = useMutation(
    dashboardv99990101Client.postCreditNote
  )
  const { mutateAsync: putCreditNote } = useMutation(
    dashboardv99990101Client.putCreditNote
  )
  const { mutateAsync: finalizeAndSendCreditNote } = useMutation(
    dashboardv99990101Client.postFinalizeAndSendCreditNote
  )

  const issueAndSend = useCallback(
    async (
      rows: CreditNoteLineItemRow[],
      {
        accountingDate,
        generateCashCreditGrant
      }: { accountingDate: string; generateCashCreditGrant: boolean }
    ): Promise<boolean> => {
      if (!invoice) {
        return false
      }

      try {
        const creditNoteId = await createCreditNoteWithLineItems(
          { postCreditNote, createLineItemGroups },
          { accountingDate, invoice, rows }
        )

        if (!creditNoteId) return false

        // The cash credit grant is a credit-note setting, so it has to be set on
        // the created draft before it is finalized; the create endpoint doesn't
        // accept settings. The update replaces the credit note's editable
        // fields, so the created values (billing period, customer, etc.) are
        // read back and sent alongside the setting — otherwise finalize-and-send
        // fails its required-field validation. Mirrors the legacy issue dialog.
        if (generateCashCreditGrant) {
          const createdCreditNote = await fetchQuery(
            apiQueryClient,
            dashboardv99990101Client.getCreditNote,
            { id: creditNoteId },
            { staleTime: 0 }
          )

          if (!createdCreditNote) return false

          await putCreditNote({
            id: creditNoteId,
            body: {
              ...createdCreditNote,
              settings: { generateCashCreditGrant: 'ENABLED' }
            }
          })
        }

        // Finalize and send (the legacy flow): emails the credit note to the
        // customer without applying it to the invoice or the ledger.
        await finalizeAndSendCreditNote({ id: creditNoteId })

        // The user stays on the invoice; the toast's view button is the way
        // into the newly created credit note. truncate:false keeps the fixed
        // copy from middle-truncating alongside the view button.
        displayNotification('Credit note issued and sent', {
          type: 'success',
          truncate: false,
          'data-testid': 'issueCreditNote.sendSuccessToast',
          viewLabel: 'View credit note',
          onClickView: () => navigate(`/credit-notes/${creditNoteId}`)
        })

        return true
      } catch (error) {
        const [firstValidationError] = extractValidationErrors(error)
        displayNotification('Failed to issue credit note', {
          type: 'error',
          description: firstValidationError
        })

        return false
      }
    },
    [
      invoice,
      postCreditNote,
      putCreditNote,
      finalizeAndSendCreditNote,
      createLineItemGroups,
      displayNotification,
      navigate
    ]
  )

  return { issueAndSend }
}
